import React, { useState, useEffect } from "react";
import {
  Box,
  TextField,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button,
  Grid,
} from "@mui/material";
import SendIcon from "@mui/icons-material/Send";
import Header from "../../components/Header";
import {
  updateWeightCalcAction,
  getWeightCalcActionFn,
} from "../../redux/actions/weightCalcAction";
import { useDispatch } from "react-redux";
import { useNavigate, useLocation } from "react-router-dom";
import { toast } from "react-toastify";
import LoadingIndicator from "../../components/loadingIndicator";
import * as yup from "yup";

const validationSchema = yup.object().shape({
  calctype: yup
    .string()
    .oneOf(["rating", "gm", "gmc"], "Invalid type")
    .required("Type is required"),
  factor: yup.string().required("Factor is required"),
  weight: yup
    .number()
    .min(0, "Weight must be at least 0")
    .max(100, "Weight cannot exceed 100")
    .required("Weight is required")
});

const EditWeightCalc = () => {
  const location = useLocation();
  const { pathname } = location;
  const _id = pathname.split("/").pop();

  const dispatch = useDispatch();
  const [err, setErr] = useState({});
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    calctype: "",
    factor: "",
    weight: ""
  });
  const navigate = useNavigate();

  useEffect(() => {
    const fetchWeightCalcDetails = async () => {
      try {
        const response = await dispatch(getWeightCalcActionFn(_id));
        if (response && response.success) {
          const weightCalcDetails = response.data;
          setFormData(weightCalcDetails);
        } else {
          console.log("Failed to fetch weightCalc details");
          toast.error("Failed to fetch weightCalc details");
        }
      } catch (error) {
        console.log("Error fetching weightCalc details", error);
        toast.error("Error fetching weightCalc details");
      }
    };

    fetchWeightCalcDetails();
  }, [_id, dispatch]);

  const handleBlur = async (e) => {
    const fieldName = e.target.name;
    try {
      await validationSchema.validateAt(fieldName, formData);
      setErr({ ...err, [fieldName]: "" });
    } catch (error) {
      setErr({ ...err, [fieldName]: error.message });
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      setLoading(true); 
      await validationSchema.validate({ ...formData }, { abortEarly: false });

      const updatedFormData = {
        ...formData
      };

      const response = await dispatch(
        updateWeightCalcAction(_id, updatedFormData)
      );
      if (response) {
        toast.success("WeightCalc updated successfully");
        setTimeout(() => {
          navigate("/weightCalc"); 
          window.location.reload(); 
        }, 100); 
      } else {
        toast.error("Failed to update WeightCalc");
      }
    } catch (error) {
      console.error("Error submitting data:", error);
    } finally {
      setLoading(false); 
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
  };

  return (
    <Box mt="1.5rem" mb="2.5rem" ml="2.5rem" mr="2.5rem">
      <Header title="EDIT WEIGHT CALCULATION" />
      <form onSubmit={handleSubmit}>
        <FormControl fullWidth margin="normal">
          {/* CALCTYPE */}
          <FormControl fullWidth variant="outlined" margin="normal">
            <InputLabel>Type</InputLabel>
            <Select
              name="calctype"
              value={formData.calctype}
              onBlur={handleBlur}
              onChange={handleChange}
            >
              <MenuItem value="rating">Rating</MenuItem>
              <MenuItem value="gm">GM</MenuItem>
              <MenuItem value="gmc">GMC</MenuItem>
            </Select>
            {err.calctype && (
              <Grid item>
                <Typography color="error">{err.calctype}</Typography>
              </Grid>
            )}
          </FormControl>

          {/* FACTOR */}
          <TextField
            label="Factor"
            name="factor"
            value={formData.factor}
            onBlur={handleBlur}
            onChange={handleChange}
            margin="normal"
          />
          {err.factor && (
            <Grid item>
              <Typography color="error">{err.factor}</Typography>
            </Grid>
          )}

          {/* WEIGHT */}
          <TextField
            label="Weight"
            name="weight"
            type="number"
            value={formData.weight}
            onBlur={handleBlur}
            onChange={handleChange}
            margin="normal"
          />
          {err.weight && (
            <Grid item>
              <Typography color="error">{err.weight}</Typography>
            </Grid>
          )}

          {/* SUBMIT BUTTON */}
          <Grid container justifyContent="center" alignItems="center">
            <Button
              type="submit"
              variant="contained"
              color="primary"
              startIcon={<SendIcon />}
              disabled={loading}
            >
              Submit
            </Button>
          </Grid>
        </FormControl>
      </form>
      {loading && <LoadingIndicator />}
    </Box>
  );
};

export default EditWeightCalc;

